import React from "react";
import { Box, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import { ConnectionStatus } from "../../types/ai-event";
import { ChatLifecyclePhase, ConnectionPhase, lifecycleCaption } from "../../connection/chatPhase";
import { AccountSwitcher } from "./AccountSwitcher";
import { CasLoader } from "../brand/CasLoader";

interface AiPanelHeaderProps {
  connectionStatus: ConnectionStatus;
  // True when the controller gave up reconnecting (a retryable CONNECTION_ERROR
  // is the latest event). Only then is "Disconnected" shown; every other
  // not-connected moment is the auto-reconnect path and shows Cas's loader.
  connectionFailed?: boolean;
  // Whether Cas can answer now (agent-chat-speed Decision 5, chatPhase.ts connectionPhase).
  // Absent → derived from the socket alone (open = "online", never green).
  phase?: ConnectionPhase;
  // The single derived lifecycle (spec Decision 2). When absent the old ConnectionPhase is used.
  lifecycle?: ChatLifecyclePhase;
  activeSessionId?: string | null;
  fullscreen?: boolean;
  historyOpen: boolean;
  onToggleHistory?: () => void;
  onToggleFullscreen?: () => void;
  onClose?: () => void;
  onClear: () => void;
}

// The caption IS the tooltip. Green only when a Session is ready; an open socket alone is a neutral
// grey "Online" (spec Decision 5); a starting workspace shows Cas's loader, as connecting does. Red
// is only "Disconnected" (U.7).
const PHASE_CAPTION: Record<ConnectionPhase, string> = {
  connecting: "Connecting…",
  failed: "Disconnected",
  online: "Online",
  provisioning: "Starting workspace…",
  ready: "Connected",
};

export const AiPanelHeader: React.FC<AiPanelHeaderProps> = ({
  connectionStatus,
  connectionFailed = false,
  phase,
  lifecycle,
  activeSessionId,
  fullscreen,
  historyOpen,
  onToggleHistory,
  onToggleFullscreen,
  onClose,
  onClear,
}) => {
  const theme = useTheme();

  // data-status stays the SOCKET state (connected | connecting | disconnected) — the existing
  // Playwright specs wait on it; data-phase is whether Cas can answer.
  const socketStatus = {
    [ConnectionStatus.CONNECTED]: "connected",
    [ConnectionStatus.CONNECTING]: "connecting",
    [ConnectionStatus.DISCONNECTED]: "disconnected",
  }[connectionStatus];
  const shownPhase: ConnectionPhase | ChatLifecyclePhase =
    lifecycle ??
    phase ??
    (connectionStatus === ConnectionStatus.CONNECTED ? "online" : connectionFailed ? "failed" : "connecting");
  const caption = lifecycle
    ? lifecycleCaption(lifecycle, activeSessionId ?? null)
    : PHASE_CAPTION[shownPhase as ConnectionPhase];
  const showLoader = lifecycle
    ? lifecycle === "connecting" || lifecycle === "resuming" || lifecycle === "loading-history" || lifecycle === "reconnecting"
    : shownPhase === "connecting" || shownPhase === "provisioning";
  const green = lifecycle
    ? lifecycle === "running" || (lifecycle === "idle" && !!activeSessionId)
    : shownPhase === "ready";
  const red = lifecycle ? lifecycle === "offline" : shownPhase === "failed";
  const dotColor = green
    ? theme.palette.success.main
    : red
    ? theme.palette.error.main
    : theme.palette.grey[400];
  const loaderLabel =
    shownPhase === "provisioning" ? "Starting workspace"
    : shownPhase === "resuming" ? "Resuming chat"
    : shownPhase === "loading-history" ? "Loading chat"
    : shownPhase === "reconnecting" ? "Reconnecting"
    : "Connecting to Cas";

  const dot = {
    width: 8,
    height: 8,
    borderRadius: "50%",
    mr: 1,
    flexShrink: 0,
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        px: 2,
        py: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      {onToggleHistory && (
      <IconButton
        size="small"
        onClick={onToggleHistory}
        data-testid="toggle-history"
        aria-label="Toggle chat history"
        sx={{
          mr: 0.5,
          color: historyOpen ? theme.palette.primary.main : theme.palette.text.secondary,
          backgroundColor: historyOpen ? `${theme.palette.primary.main}14` : "transparent",
        }}
      >
        <ViewSidebarOutlinedIcon fontSize="small" />
      </IconButton>
      )}
      <Tooltip title={caption}>
        <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
          {showLoader ? (
            <Box data-testid="conn-dot" data-status={socketStatus} data-phase={shownPhase} sx={{ display: "inline-flex", mr: 0.5 }}>
              <CasLoader size={14} label={loaderLabel} />
            </Box>
          ) : (
            <Box data-testid="conn-dot" data-status={socketStatus} data-phase={shownPhase} sx={{ ...dot, mr: 0.5 }} style={{ backgroundColor: dotColor }} />
          )}
          <Typography variant="caption" data-testid="conn-caption" sx={{ color: theme.palette.text.secondary }}>{caption}</Typography>
        </Box>
      </Tooltip>
      <AccountSwitcher />
      <Box sx={{ flex: 1 }} />
      <IconButton size="small" onClick={onClear}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
      {onToggleFullscreen && (
        <IconButton size="small" onClick={onToggleFullscreen}>
          {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
        </IconButton>
      )}
      {onClose && (
        <IconButton size="small" onClick={onClose} data-testid="ai-panel-close" aria-label="Close chat">
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
